import { BareButton } from '@calendly/ui/components/bare-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { mergeRefs } from '@calendly/ui/utils/merge-refs';
import classNames from 'classnames';
import { useRef, forwardRef, useState, useLayoutEffect, useMemo } from 'react';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { useLeftNavigationState, useTextWidth } from './hooks';
import { useExpandPhaseContext } from './left_navigation_context';
const baseContainerStatePhoneCss = "b1ngakwk";
const baseContainerStatePhoneExpandedCss = "bnlgrdc";
const baseContainerStateDefaultCss = "bggvv52";
const baseContainerStateMinimizedCss = "bukyv0f";
const baseContainerStateExpandedCss = "b1hi1c7l";
const baseContainerCss = "bx30w11";

/**
 * Root container for the left nav; width and transition are driven by state.
 * @param {Object} props
 * @param {string} [props.state] - Gives CSS state to control width.
 */
export const BaseContainer = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(baseContainerCss, {
    [baseContainerStatePhoneCss]: state?.toString() === 'phone',
    [baseContainerStatePhoneExpandedCss]: state?.toString() === 'phone-expanded',
    [baseContainerStateDefaultCss]: state?.toString() === 'default',
    [baseContainerStateMinimizedCss]: state?.toString() === 'minimized',
    [baseContainerStateExpandedCss]: state?.toString() === 'expanded'
  }, props.className)} />;
});
const navigationIconContainerCss = "ntces65";
export const NavigationIconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationIconContainerCss, props.className)} />;
});
const navigationLabelCss = "n2wybsz";
export const NavigationLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(navigationLabelCss, props.className)} />;
});
const titleCss = "t1piqhvi";
export const Title = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleCss, props.className)} />;
});
const subtitleCss = "suivsuk";
export const Subtitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subtitleCss, props.className)} />;
});

/**
 * Temporary: only for previewing the new app shell with the current left nav. Drop this class and
 * the `useProductRefreshAppShell` branch on `Wrapper` once we ship Main Nav in place of this nav.
 */
const wrapperAppShellCss = "w1xxx3t6";
const wrapperStatePhoneCss = "w19l5nla";
const wrapperStateDefaultCss = "w1bi7z02";
const wrapperStateMinimizedCss = "w1eelh80";
const wrapperStateExpandedCss = "w1av3mtd";
const wrapperStatePhoneExpandedCss = "wgzghvc";

/*
 * `position: fixed` escapes the normal layout flow entirely: `top`/`height` are
 * resolved against the viewport, not against any ancestor's size or offset. So
 * pinning this to `top: 0; height: 100dvh` means it always covers the literal
 * top of the browser viewport — including painting over (at z-index 10) an
 * in-flow Signadot sandbox banner rendered above `#root` by the host page,
 * since that banner has no competing z-index and this nav sits above it.
 * `--signadot-banner-height` (set by the host page; see `publisher.html.haml`)
 * shifts this down by the banner's real height and shrinks it by the same
 * amount so it still ends exactly at the viewport bottom. Defaults to `0px`
 * everywhere the banner isn't present.
 */
const wrapperCss = "w1k81ni1";
export const Wrapper = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'nav';
  const isProductRefreshAppShell = useProductRefreshAppShell();
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, {
    [wrapperAppShellCss]: isProductRefreshAppShell,
    [wrapperStatePhoneCss]: state?.toString() === 'phone',
    [wrapperStateDefaultCss]: state?.toString() === 'default',
    [wrapperStateMinimizedCss]: state?.toString() === 'minimized',
    [wrapperStateExpandedCss]: state?.toString() === 'expanded',
    [wrapperStatePhoneExpandedCss]: state?.toString() === 'phone-expanded'
  }, props.className)} />;
});
const overlayNoBackgroundCss = "ojrz821";

/*
 * Phone-nav dimming backdrop, sibling to `wrapperCss` above. Same
 * `position: fixed` reasoning applies: without offsetting by
 * `--signadot-banner-height`, this always paints over the in-flow Signadot
 * banner (z-index 9 vs. the banner's unset z-index) even though the drawer
 * itself (`wrapperCss`) no longer does. Mirrors `mobileNavOverlayCss` in
 * `AppLayout.tsx`.
 */
const overlayCss = "ocx3egh";
export const Overlay = forwardRef((props, ref) => {
  const {
    noBackground,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(overlayCss, {
    [overlayNoBackgroundCss]: noBackground
  }, props.className)} />;
});
const topWrapperCss = "t5b6vy6";

/** Top bar padding when nav is minimized (collapsed). */
export const topWrapperMinimizedCss = "tjfsgpv";

/** Phone-only: top bar padding for mobile left nav menu. */
export const topWrapperPhoneCss = "t1l0y0jt";

/** During collapse: keep logo left-aligned with consistent padding so the "C" doesn’t jump. */
export const topWrapperCollapsingCss = "t2epqcz";
export const TopWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topWrapperCss, props.className)} />;
});
const toggleButtonIsPhoneCss = "t14lgt0q";
const toggleButtonIsRelatedPositioningCss = "t1jztoua";
const toggleButtonCss = "tz91nwe";
export const ToggleButton = forwardRef((props, ref) => {
  const {
    isRelatedPositioning,
    ...restProps
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <BareButton ref={ref} {...restProps} className={classNames(toggleButtonCss, {
    [toggleButtonIsPhoneCss]: isPhone,
    [toggleButtonIsRelatedPositioningCss]: isRelatedPositioning
  }, props.className)} />;
});
const globalCreateWrapperCss = "g10a98hy";
export const globalCreateWrapperMinimizedCss = "g13u6av1";

/** Wrapper around the Create button; use globalCreateWrapperMinimizedCss when nav is minimized. */
export const GlobalCreateWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(globalCreateWrapperCss, props.className)} />;
});
const navigationItemsCss = "n1vb3wl";
export const NavigationItems = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationItemsCss, props.className)} />;
});
const navigationAnchorItemBaseIsMultilineCss = "n1lmldd3";
const navigationAnchorItemBaseIsMinimizedCss = "ng5lpu0";
const navigationAnchorItemBaseIsActiveCss = "nz5m5pm";
const navigationAnchorItemBaseIsHoveredCss = "n1fqmonv";
const navigationAnchorItemBaseIsCollapsingCss = "nstuhj3";
const navigationAnchorItemBaseIsMinimizedLayoutCss = "nd6zm0x";
const navigationAnchorItemBaseIsExpandingFadeOutCss = "n1ao7m2y";
const navigationAnchorItemBaseIsExpandingFadeInPendingCss = "n6xl302";
const navigationAnchorItemBaseIsExpandingFadeInCss = "nf4gkwg";
export const navigationLabelCaptionMinimizedCss = "nopx8k5";

/* Shared fade-in: 250ms hold then 50ms fade (300ms total). Delay baked into keyframes so expand/collapse match. */

/* Fade-out duration so collapse/expand visibly fade to 0 (animation from 1→0, no prior frame needed). */
const NAV_ITEM_FADEOUT_MS = 150;

/* Minimized layout dimensions (shared so collapse/expand phases stay in sync). */
export const NAV_ITEM_MINIMIZED_MARGIN_H = 10;
/* Button (e.g. Help, Upgrade CTA) in collapsed state: 67px so 67 + 10 + 10 margin = 87px (88 - 1 border). */
export const NAV_ITEM_MINIMIZED_BUTTON_WIDTH_PX = 67;

/** Width/center for nav buttons in minimized layout only. Reused by NavigationAnchorItem (as=button) and UpgradeCTA. */
export const navButtonMinimizedWidthCss = "n1ixkcz9";

/** Class name only; layout lives inside navigationAnchorItemBaseCss so it overrides base styles when both apply. */
const navItemMinimizedLayoutSharedCss = "nvwzemk";

/* Nav item — hover colors as variables so :hover and .IsHoveredCss (parent-reported hover) share one definition */

const navigationAnchorItemBaseCss = "n1o6jz44";

/** Delay (ms) before nav collapses; items animate fade out at full width, then width and minimized layout apply. */
export const COLLAPSE_FADEOUT_MS = NAV_ITEM_FADEOUT_MS;

/** Wrapper that applies the same phase fade as nav items. Use so the inner content (e.g. Upgrade CTA button) doesn't need phase opacity. */
const navItemFadeWrapperCss = "nw3boip";
export const NavItemFadeWrapper = ({
  children,
  isCollapsing,
  isMinimizedLayout,
  isExpandingFadeOut,
  isExpandingFadeInPending,
  isExpandingFadeIn
}) => <div className={classNames(navItemFadeWrapperCss, {
  [navigationAnchorItemBaseIsCollapsingCss]: isCollapsing,
  [navigationAnchorItemBaseIsMinimizedLayoutCss]: isMinimizedLayout,
  [navigationAnchorItemBaseIsExpandingFadeOutCss]: isExpandingFadeOut,
  [navigationAnchorItemBaseIsExpandingFadeInPendingCss]: isExpandingFadeInPending,
  [navigationAnchorItemBaseIsExpandingFadeInCss]: isExpandingFadeIn
})}>
    {children}
  </div>;
const NavigationAnchorItemBase = forwardRef((props, ref) => {
  const {
    isMultiline,
    isMinimized,
    isCollapsing,
    isExpandingFadeOut,
    isExpandingFadeInPending,
    isExpandingFadeIn,
    isActive,
    isHovered,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'a';
  /* When isExpandingFadeOut we use only isExpandingFadeOutCss for layout + fade-out; do NOT apply
   * isMinimizedLayoutCss or its fade-in animation would override the expand fade-out. */
  const isMinimizedLayout = isMinimized && !isCollapsing && !isExpandingFadeOut;
  const useMinimizedLayoutShared = isMinimizedLayout || isExpandingFadeOut || isExpandingFadeInPending;
  return <Component ref={ref} {...restProps} className={classNames(navigationAnchorItemBaseCss, {
    [navItemMinimizedLayoutSharedCss]: useMinimizedLayoutShared,
    [navButtonMinimizedWidthCss]: useMinimizedLayoutShared && AsComponent === BareButton,
    [navigationAnchorItemBaseIsMultilineCss]: isMultiline,
    [navigationAnchorItemBaseIsMinimizedCss]: isMinimized,
    [navigationAnchorItemBaseIsCollapsingCss]: isCollapsing,
    [navigationAnchorItemBaseIsExpandingFadeOutCss]: isExpandingFadeOut,
    [navigationAnchorItemBaseIsExpandingFadeInPendingCss]: isExpandingFadeInPending,
    [navigationAnchorItemBaseIsExpandingFadeInCss]: isExpandingFadeIn,
    [navigationAnchorItemBaseIsActiveCss]: isActive,
    [navigationAnchorItemBaseIsHoveredCss]: isHovered,
    [navigationAnchorItemBaseIsMinimizedLayoutCss]: isMinimizedLayout
  }, props.className)} />;
});

/**
 * Nav link (icon + label) with collapse behavior.
 * Fades out at full width, then uses minimized layout when width collapses.
 * On expand: fade out in minimized layout, then fade in in expanded layout.
 * Forwards ref to the anchor so parent can forward clicks from wrapper.
 *
 * @param {Object} props
 * @param {boolean} [props.isHovered] - When true, applies hover styles (used when parent wrapper is hovered)
 * @param {React.Ref<HTMLElement>|undefined|null} ref - Ref forwarded to the anchor (object or callback ref)
 */
export const NavigationAnchorItem = forwardRef((props, ref) => {
  const state = useLeftNavigationState();
  const {
    isExpanding,
    expandFadeOutDone,
    expandFadeInStarted
  } = useExpandPhaseContext();
  const internalRef = useRef(null);
  const mergedRef = useMemo(() => mergeRefs(internalRef, ref), [internalRef, ref]);
  const isMultiline = useTextWidth(internalRef, 175);
  const isMinimized = state === 'minimized';
  const [isCollapsing, setIsCollapsing] = useState(false);
  const isExpandingFadeOut = isExpanding && !expandFadeOutDone;
  const isExpandingFadeInPending = isExpanding && expandFadeOutDone && !expandFadeInStarted;
  const isExpandingFadeIn = isExpanding && expandFadeOutDone && expandFadeInStarted;

  /* useLayoutEffect so we set isCollapsing true before paint; otherwise first paint shows minimized layout and no fade-out */
  useLayoutEffect(() => {
    if (!isMinimized) {
      setIsCollapsing(false);
      return undefined;
    }
    setIsCollapsing(true);
    const t = setTimeout(() => setIsCollapsing(false), COLLAPSE_FADEOUT_MS);
    return () => clearTimeout(t);
  }, [isMinimized]);
  return <NavigationAnchorItemBase ref={mergedRef} {...props} isMultiline={isMultiline} isMinimized={isMinimized} isCollapsing={isCollapsing} isExpandingFadeOut={isExpandingFadeOut} isExpandingFadeInPending={isExpandingFadeInPending} isExpandingFadeIn={isExpandingFadeIn} />;
});
const navigationWrapperCss = "nwmaplx";

/** Scrollable column that holds the main nav links (scheduling, contacts, etc.). */
export const NavigationWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationWrapperCss, props.className)} />;
});
const topNavigationCss = "t167ofs6";
export const TopNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topNavigationCss, props.className)} />;
});
const bottomNavigationCss = "b10utj7k";
export const BottomNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bottomNavigationCss, props.className)} />;
});
const loaderContainerCss = "l16z40no";
export const LoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(loaderContainerCss, props.className)} />;
});

require("./elements.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./elements.js");